import React from 'react';
import { Sliders, Layers, TrendingUp, LayoutGrid, Terminal, Lock, ChevronDown, HelpCircle, BookOpen } from 'lucide-react';
import type { MacroGateSummary, BreadthSummary } from '../types';

interface HeaderProps {
  macro: MacroGateSummary;
  breadth: BreadthSummary;
  activeTab: 'macro' | 'scanner' | 'sectors' | 'studio' | 'guide';
  setActiveTab: (tab: 'macro' | 'scanner' | 'sectors' | 'studio' | 'guide') => void;
  selectedProfile: string;
  setSelectedProfile: (profile: string) => void;
  onResetToLanding?: () => void;
  onOpenCheatSheet?: () => void;
}

const MACRO_PHASE_LABEL: Record<string, string> = {
  A1: '升市初段',
  A2: '升市中段',
  A3: '升市末段',
  B1: '跌市初段',
  B2: '跌市中段',
  B3: '跌市末段',
};

export const Header: React.FC<HeaderProps> = ({
  macro,
  breadth,
  activeTab,
  setActiveTab,
  selectedProfile,
  setSelectedProfile,
  onResetToLanding,
  onOpenCheatSheet,
}) => {
  const handleLogoClick = () => {
    if (onResetToLanding) {
      onResetToLanding();
    } else {
      setActiveTab('macro');
    }
  };

  return (
    <header className="border-b border-[#182232] bg-[#0b0f17] sticky top-0 z-40">
      {/* Top Global Institutional Telemetry App Bar */}
      <div className="w-full h-11 px-3 md:px-4 flex items-center justify-between text-xs bg-[#0b0f17]">
        {/* Brand Anchor (Clickable Logo) */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="site-logo-btn"
            onClick={handleLogoClick}
            className="flex items-center gap-2 group text-left cursor-pointer focus:outline-none transition-transform active:scale-98"
            title="返回宏觀首頁 (Return to Landing Page)"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-terminal-cyan opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-terminal-cyan"></span>
            </span>
            <span className="text-xs md:text-sm font-bold tracking-tight text-slate-100 uppercase group-hover:text-terminal-cyan transition-colors">
              KOSTOLANY EGG INTELLIGENCE
            </span>
            <span className="hidden sm:inline-block text-xs font-medium text-sky-400/90 group-hover:text-sky-300 transition-colors">
              (科斯托蘭尼雞蛋理論雙層決策系統)
            </span>
          </button>

          <span
            data-testid="header-timestamp"
            className="hidden xl:inline-block px-2 py-0.5 rounded text-[11px] font-mono bg-[#141923] text-slate-300 border border-[#1e293b]/70"
          >
            {macro.trade_date} 15:30 盤後定價結算
          </span>
        </div>

        {/* Institutional Telemetry Chips */}
        <div className="hidden lg:flex items-center gap-2">
          {/* Market Regime Badge */}
          <div
            data-testid="header-regime"
            className="flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-blue-950/60 border border-blue-500/40 text-terminal-cyan"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-terminal-cyan animate-pulse"></span>
            <span className="text-[11px] font-medium text-sky-200/80">宏觀週期: </span>
            <span className="text-[11px] font-mono font-bold text-terminal-cyan">
              {macro.macro_phase} {MACRO_PHASE_LABEL[macro.macro_phase] ?? '升市中段'}
            </span>
          </div>

          {/* Breadth Buy/Hold Share */}
          <div
            data-testid="header-breadth"
            className="flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-[#141923] border border-[#1e293b]/70"
          >
            <span className="text-[11px] font-medium text-slate-300">做多/持有比例:</span>
            <span className="text-[11px] font-mono font-bold text-terminal-cyan">
              {(breadth.pct_buy_zone * 100).toFixed(1)}%
            </span>
          </div>

          {/* Max Portfolio Equity Cap */}
          <div
            data-testid="header-max-cap"
            className="flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-[#141923] border border-[#1e293b]/70"
          >
            <span className="text-[11px] font-medium text-slate-300">權益上限:</span>
            <span className="text-[11px] font-mono font-semibold text-slate-100">
              {(macro.max_equity_cap * 100).toFixed(0)}%
            </span>
            <div className="w-12 h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-container"
                style={{ width: `${Math.min(100, macro.max_equity_cap * 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Sizing Multiplier */}
          <div
            data-testid="header-multiplier"
            className="flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-[#141923] border border-[#1e293b]/70"
          >
            <span className="text-[11px] font-medium text-slate-300">單筆權重:</span>
            <span className="text-[11px] font-mono text-slate-100 font-bold">
              {macro.macro_sizing_multiplier.toFixed(1)}x
            </span>
          </div>
        </div>

        {/* Right Actions: Functional Model Preset Selector + ML Studio + Cheat Sheet Help */}
        <div className="flex items-center gap-2">
          <div className="relative" title="切換模型預設參數組 (即時重算全市場 1,826 檔階段)">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-[#141923] hover:bg-[#1c2230] border border-[#1e293b]/80 text-slate-200 transition-colors">
              <Lock className="w-3 h-3 text-terminal-cyan" />
              <select
                data-testid="header-profile-select"
                value={selectedProfile}
                onChange={(e) => setSelectedProfile(e.target.value)}
                aria-label="選擇模型參數組"
                className="bg-transparent text-[11px] font-mono text-slate-200 focus:outline-none cursor-pointer pr-4 appearance-none"
              >
                <option value="Default Kostolany v1" className="bg-[#141923] text-slate-200">
                  Default Kostolany v1
                </option>
                <option value="ML-Optimized Bull Momentum" className="bg-[#141923] text-slate-200">
                  ML-Optimized Bull Momentum
                </option>
                <option value="ML-Optimized Defensive Bear" className="bg-[#141923] text-slate-200">
                  ML-Optimized Defensive Bear
                </option>
              </select>
              <ChevronDown className="w-3 h-3 text-slate-400 pointer-events-none -ml-3" />
            </div>
          </div>

          <div className="flex items-center gap-1.5 pl-1 text-slate-300">
            <button
              type="button"
              data-testid="header-studio-btn"
              onClick={() => setActiveTab('studio')}
              className={`px-2 py-1 rounded flex items-center gap-1 text-[11px] font-mono transition-colors cursor-pointer ${
                activeTab === 'studio'
                  ? 'bg-primary-container text-white'
                  : 'bg-[#141923] hover:bg-[#1c2230] text-slate-200 border border-[#1e293b]/80'
              }`}
              title="開啟參數與 ML 工作室 (微調模型閥值與回測)"
            >
              <Sliders className="w-3.5 h-3.5 text-terminal-cyan" />
              <span className="hidden md:inline">參數調校</span>
            </button>

            <button
              type="button"
              data-testid="header-guide-btn"
              onClick={() => setActiveTab('guide')}
              className={`px-2 py-1 rounded flex items-center gap-1 text-[11px] font-mono transition-colors cursor-pointer ${
                activeTab === 'guide'
                  ? 'bg-primary-container text-white'
                  : 'bg-[#141923] hover:bg-[#1c2230] text-slate-200 border border-[#1e293b]/80'
              }`}
              title="查看完整實戰操作手冊與回測全書"
            >
              <BookOpen className="w-3.5 h-3.5 text-terminal-cyan" />
              <span className="hidden md:inline">實戰手冊</span>
            </button>

            <button
              type="button"
              data-testid="header-help-btn"
              onClick={onOpenCheatSheet}
              className="px-2.5 py-1 rounded bg-sky-950/70 hover:bg-sky-900/80 text-sky-300 border border-sky-500/40 flex items-center gap-1 text-[11px] font-mono font-semibold transition-colors cursor-pointer"
              title="開啟科斯托蘭尼雞蛋速查表 (Cheat Sheet)"
            >
              <HelpCircle className="w-3.5 h-3.5 text-terminal-cyan" />
              <span>速查表</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sub Navigation Bar (Mobile / Tablet only — Desktop uses Sidebar + Workspace Tab Bar) */}
      <div className="lg:hidden flex items-center justify-between px-3 py-1.5 bg-[#0c0e16]/95">
        <div className="flex items-center gap-2">
          <span className="hidden md:inline-flex items-center gap-1 text-[11px] font-mono text-sky-400/85">
            <Terminal className="w-3.5 h-3.5 text-terminal-cyan" />
            <span>TWSE/TPEx Institutional Core</span>
          </span>
        </div>

        <nav className="flex items-center gap-1 bg-[#0b0f17] p-1 rounded border border-[#182232] overflow-x-auto">
          <button
            data-testid="tab-macro"
            onClick={() => setActiveTab('macro')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-mono font-medium transition-all ${
              activeTab === 'macro'
                ? 'bg-primary-container text-white shadow-sm'
                : 'text-slate-300 hover:text-white hover:bg-[#161b26]'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>宏觀</span>
          </button>

          <button
            data-testid="tab-scanner"
            onClick={() => setActiveTab('scanner')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-mono font-medium transition-all ${
              activeTab === 'scanner'
                ? 'bg-primary-container text-white shadow-sm'
                : 'text-slate-300 hover:text-white hover:bg-[#161b26]'
            }`}
          >
            <TrendingUp className="w-3.5 h-3.5" />
            <span>掃描</span>
          </button>

          <button
            data-testid="tab-sectors"
            onClick={() => setActiveTab('sectors')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-mono font-medium transition-all ${
              activeTab === 'sectors'
                ? 'bg-primary-container text-white shadow-sm'
                : 'text-slate-300 hover:text-white hover:bg-[#161b26]'
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
            <span>板塊</span>
          </button>

          <button
            data-testid="tab-studio"
            onClick={() => setActiveTab('studio')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-mono font-medium transition-all ${
              activeTab === 'studio'
                ? 'bg-primary-container text-white shadow-sm'
                : 'text-slate-300 hover:text-white hover:bg-[#161b26]'
            }`}
          >
            <Sliders className="w-3.5 h-3.5" />
            <span>回測</span>
          </button>

          <button
            data-testid="tab-guide"
            onClick={() => setActiveTab('guide')}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-mono font-medium transition-all ${
              activeTab === 'guide'
                ? 'bg-primary-container text-white shadow-sm'
                : 'text-slate-300 hover:text-white hover:bg-[#161b26]'
            }`}
          >
            <BookOpen className="w-3.5 h-3.5" />
            <span>手冊</span>
          </button>
        </nav>
      </div>
    </header>
  );
};
